import React from 'react';

import { dealToProduct } from '../../../../analytics-library/entry/dealDataToProducts';
import { EventAction, EventArea, sendEvent } from '../../../../analytics-library/entry/sendEvent';
import { WidgetInteracted } from '../../../../analytics-library/events/WidgetInteracted';
import { apstagAttributes, getAsin } from '../../../../sharedModules/apstag/getAttributes';
import { LegacyActionEvent } from '../../../../types/LegacyActionEvent';
import { useHawkWidgetContext } from '../HawkWidget/HawkWidgetContext';
import { TrackedLinkAttributesProp } from '../TrackedLink/model';

import type { AffiliateLinkProps } from './model';
import styles from './styles/affiliate-link.css';

const AffiliateLink: React.FC<AffiliateLinkProps> = ({
  deal,
  label,
  actionType,
  linkClick,
  children,
  className,
  customTarget,
  customUrl,
  renderPlainChildren = true,
  areaClicked,
}) => {
  const {
    sendAnalytics,
    translate,
    genericSharedComponents: { TrackedLink },
    dealData,
    widgetType,
    result,
  } = useHawkWidgetContext();

  const linkRef = React.useRef<null | HTMLAnchorElement>(null);

  // Avoid rendering an empty <a> tag in all cases
  if (!children) {
    return null;
  }

  const link = customUrl ?? (label === 'Free trial' ? deal?.shipping.url : deal?.offer.link) ?? '';

  const trackLinkClick = (): void => {
    sendAnalytics({
      affiliate: {
        action: {
          type: actionType,
          event: LegacyActionEvent.CLICK_FROM,
        },
        component: {
          areaClicked,
          ...(label && { label }), // Overwrite the label if it's provided - used by prime shipping 'Free Trial'
        },
      },
      deal,
      product: {
        product: {
          primary: {
            link,
          },
        },
      },
      dealData,
    });
    if (deal) {
      sendEvent(
        new WidgetInteracted({
          area: areaClicked ?? EventArea.UNSPECIFIED,
          action: EventAction.AFFILIATE_LINK_CLICKED,
          widget: {
            type: widgetType,
            result,
            products: [dealToProduct(deal, dealData.modelInfo)],
          },
        }),
      );
    }

    if (linkClick && deal) {
      linkClick(deal);
    }
  };
  const ariaLabelModel =
    deal?.model && deal.model !== 'Everything' ? deal.model : deal?.model_matched?.join(',');

  if (!link || link === '#') {
    return <>{renderPlainChildren ? children : null}</>;
  }

  const affiliateLinkClassName = className && styles[className] ? styles[className] : className;

  const attributes: TrackedLinkAttributesProp = {
    ref: linkRef,
    href: link,
    referrerPolicy: 'no-referrer-when-downgrade',
    className: affiliateLinkClassName || styles['container'],
    'data-product-key': deal?.product_key,
    'data-url': deal?.offer?.link,
    'data-model-id': deal?.model_id,
    'data-match-id': deal?.id,
    'data-product-type': deal?.product_type,
    'data-link-merchant': deal?.merchant.name,
    'data-merchant-id': deal?.merchant?.id,
    'data-merchant-name': deal?.merchant?.name,
    'data-merchant-url': deal?.merchant?.url,
    'data-merchant-network': deal?.an,
    'aria-label':
      (ariaLabelModel &&
        deal?.merchant.name &&
        translate('ariaLabelText', [ariaLabelModel, deal.merchant.name])) ||
      translate('alternativeLinkText'),
  };

  const asin = getAsin({
    merchantName: deal?.merchant.name,
    productKey: deal?.product_key,
  });

  if (asin) {
    attributes[apstagAttributes.asin] = asin;
  }

  return (
    <TrackedLink
      trackLinkClick={trackLinkClick}
      customTarget={customTarget}
      attributes={attributes}
    >
      {children}
    </TrackedLink>
  );
};

export default AffiliateLink;
